import React from 'react';
import { Button, Card, OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import Moment from 'react-moment';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  PanelField,
  FurtherConsiderations,
  PanelMarker,
  Section,
} from '../../models';
import { UserProfile } from '../../models/UserProfile';
import { DisclaimerSection } from './disclaimer-section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { faFileDownload as faFileDownloadSolid } from '@fortawesome/pro-solid-svg-icons';
import { faThumbsUp, faThumbsDown } from '@fortawesome/free-solid-svg-icons';
import { BloodworkTestAnalysisSection } from './bloodwork-test-analysis-section';
import { GenericModal } from '../common/generic-modal';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { BloodworkNotes } from './bloodwork-notes';
import { SummaryDashboardSection } from './summary-dashboard-section';

const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');

type Props = {
  currentUserProfile: UserProfile;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  furtherConsiderations: FurtherConsiderations;
  bloodworkEntryStore: BloodworkEntryStore;
};

type State = {
  previousBloodwork: Bloodwork;
  collectionDate: string;
  bloodworkAnalysis: BloodworkAnalysis;
  showUpgradeModal: boolean;
  hideTestResultsAnalysis: boolean;
  collapsedSections: Section[];
};

export class BloodworkTestAnalysis extends React.Component<Props, State> {
  state: State = {
    previousBloodwork: undefined,
    collectionDate: '',
    bloodworkAnalysis: undefined,
    showUpgradeModal: false,
    hideTestResultsAnalysis: false,
    collapsedSections: [],
  };
  pdfWindow: Window;
  private previousBloodworkAnalysis?: BloodworkAnalysis;

  componentWillReceiveProps(nextProps: Props) {
    console.log('BloodworkTestAnalysis.componentWillReceiveProps......');
    console.log(nextProps);
    const { bloodwork, previousBloodwork } = nextProps;
    this.analyze(bloodwork, previousBloodwork);
  }

  analyze(bloodwork: Bloodwork, previousBloodwork: Bloodwork) {
    const { bloodworkRanges } = this.props;
    if (bloodwork && bloodworkRanges && bloodworkRanges.length > 0) {
      const bloodworkAnalysis = new BloodworkAnalysis({
        bloodwork,
        bloodworkRanges,
        bloodworkFields: PanelField.ALL_FIELDS,
      });
      bloodworkAnalysis.init().performAnalysis();

      if (previousBloodwork) {
        const previousBloodworkAnalysis = new BloodworkAnalysis({
          bloodwork: previousBloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        previousBloodworkAnalysis.init();
        bloodworkAnalysis.preparePreviousPanelMarkers(
          previousBloodworkAnalysis,
        );
        this.previousBloodworkAnalysis = previousBloodworkAnalysis;
      } else {
        this.previousBloodworkAnalysis = undefined;
      }

      this.setState({
        bloodworkAnalysis,
        previousBloodwork: previousBloodwork
          ? new Bloodwork(previousBloodwork)
          : undefined,
      });
      this.scrollToAnalyzeTestResults();
    }
  }

  scrollToAnalyzeTestResults() {
    let element = document.getElementById('test-results-analysis-panel');
    if (element) {
      element.scrollIntoView();
    } else {
      // Hack to resolve issue with scrolling down the first time.
      setTimeout(() => {
        element = document.getElementById('test-results-analysis-panel');
        if (element) {
          element.scrollIntoView();
        }
      }, 1000);
    }
  }

  export = (withNotes = false) => {
    console.log('export button clicked.....');
    const { currentUserProfile } = this.props;
    const { bloodworkAnalysis, previousBloodwork, collapsedSections } = this.state;

    // Check to make sure user has this feature enabled
    if (!currentUserProfile.pdfExportAllowed) {
      this.setState({
        showUpgradeModal: true,
      });
    } else {
      sessionStorage.setItem(
        'bloodwork',
        JSON.stringify(bloodworkAnalysis.bloodwork),
      );
      if (previousBloodwork) {
        sessionStorage.setItem(
          'previousBloodwork',
          JSON.stringify(previousBloodwork),
        );
      } else {
        sessionStorage.removeItem('previousBloodwork');
      }
      if (withNotes) {
        sessionStorage.setItem('exportWithNotes', 'true');
      } else {
        sessionStorage.removeItem('exportWithNotes');
      }
      if (collapsedSections.length > 0) {
        sessionStorage.setItem('collapsedSections', JSON.stringify(collapsedSections));
      } else {
        sessionStorage.removeItem('collapsedSections');
      }
      if (this.pdfWindow) {
        this.pdfWindow.close();
      }
      this.pdfWindow = window.open(
        '/bloodwork/analyze/pdf',
        'targetWindow',
        'toolbar=no, location=no, status=no, menubar=no, scrollbars=no, resizable=no, width=1028,height=764',
      );
    }
  };

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Click this icon to export to PDF with notes</div>
      </Tooltip>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openWindow = () => {
    window.open('https://labsmarts.com/testresultsanalysis');
  };

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  toggleSection = (section: Section) => {
    this.setState(prev => {
      const already = prev.collapsedSections.includes(section);
      return {
        collapsedSections: already
          ? prev.collapsedSections.filter(s => s !== section)
          : [...prev.collapsedSections, section],
      };
    });
  };

  toggleTestResultsAnalysis() {
    this.setState({
      hideTestResultsAnalysis: !this.state.hideTestResultsAnalysis,
    });
  }

  private getSectionStatus(markers: PanelMarker[]): 'green' | 'yellow' | 'orange' | 'red' {
    const isHigherIsBetter = (pm: PanelMarker) => pm.isEGFR?.() || pm.field?.useEGFRChart;
    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;
    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    let worst: 'green' | 'yellow' | 'orange' | 'red' = 'green';
    for (const pm of markers) {
      if (!pm.hasValue()) continue;
      if (!isHigherIsBetter(pm) && pm.isGreaterThanAlarm?.()) return 'red';
      if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isLessThanAlarm?.()) return 'red';
      if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1) return 'red';
      if (!isHigherIsBetter(pm) && pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) { worst = 'orange'; continue; }
      if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.()) { worst = 'orange'; continue; }
      if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2) { worst = 'orange'; continue; }
      if (worst === 'green') {
        if (!isHigherIsBetter(pm) && pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) worst = 'yellow';
        else if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.()) worst = 'yellow';
        else if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3) worst = 'yellow';
      }
    }
    return worst;
  }

  private renderSectionSummary() {
    const { bloodworkAnalysis, hideTestResultsAnalysis } = this.state;
    if (!bloodworkAnalysis || hideTestResultsAnalysis) return null;

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    const statusToVariant = (s: 'green' | 'yellow' | 'orange' | 'red'): 'success' | 'warning' | 'danger' =>
      ({ green: 'success', yellow: 'warning', orange: 'warning', red: 'danger' } as const)[s];
    const statusToStyle = (s: 'green' | 'yellow' | 'orange' | 'red'): React.CSSProperties => {
      if (s === 'green')  return { backgroundColor: '#78F880', borderColor: '#78F880', color: '#212529', backgroundImage: 'none', fontWeight: 'bold', fontSize: '12px', letterSpacing: '0.04em' };
      if (s === 'yellow') return { backgroundColor: '#FEFA83', borderColor: '#FEFA83', color: '#212529', backgroundImage: 'none', fontWeight: 'bold', fontSize: '12px', letterSpacing: '0.04em' };
      if (s === 'orange') return { backgroundColor: '#FFD37E', borderColor: '#FFD37E', color: '#212529', backgroundImage: 'none', fontWeight: 'bold', fontSize: '12px', letterSpacing: '0.04em' };
      if (s === 'red')    return { backgroundColor: '#FC6552', borderColor: '#FC6552', color: '#fff', backgroundImage: 'none', fontWeight: 'bold', fontSize: '12px', letterSpacing: '0.04em' };
      return {};
    };

    const sections: Array<{ section: Section; label: string; markers: PanelMarker[]; show?: boolean; prevMarkers: PanelMarker[] }> = [
      { section: Section.RBC,                           label: 'Blood Oxygen',        markers: bloodworkAnalysis.getRBCPanelMarkers(),                           prevMarkers: this.previousBloodworkAnalysis?.getRBCPanelMarkers() ?? [] },
      { section: Section.IRON,                          label: 'Iron Status',         markers: bloodworkAnalysis.getIronPanelMarkers(),                          prevMarkers: this.previousBloodworkAnalysis?.getIronPanelMarkers() ?? [] },
      { section: Section.WBC,                           label: 'Immune',              markers: bloodworkAnalysis.getWBCPanelMarkers(), show: hasWbcValue,         prevMarkers: this.previousBloodworkAnalysis?.getWBCPanelMarkers() ?? [] },
      { section: Section.PLATELETS,                     label: 'Blood Clotting',      markers: bloodworkAnalysis.getPlateletPanelMarkers(),                      prevMarkers: this.previousBloodworkAnalysis?.getPlateletPanelMarkers() ?? [] },
      { section: Section.GLUCOSE,                       label: 'Glucose',             markers: bloodworkAnalysis.getGlucosePanelMarkers(),                       prevMarkers: this.previousBloodworkAnalysis?.getGlucosePanelMarkers() ?? [] },
      { section: Section.KIDNEY,                        label: 'Kidney',              markers: bloodworkAnalysis.getKidneyPanelMarkers(),                        prevMarkers: this.previousBloodworkAnalysis?.getKidneyPanelMarkers() ?? [] },
      { section: Section.PROTEINS,                      label: 'Proteins',            markers: bloodworkAnalysis.getProteinsPanelMarkers(),                      prevMarkers: this.previousBloodworkAnalysis?.getProteinsPanelMarkers() ?? [] },
      { section: Section.NUTRIENTS,                     label: 'Vitamins & Minerals', markers: bloodworkAnalysis.getMineralPanelMarkers(),                       prevMarkers: this.previousBloodworkAnalysis?.getMineralPanelMarkers() ?? [] },
      { section: Section.ELECTROLYTES,                  label: 'Electrolytes',        markers: bloodworkAnalysis.getElectrolytePanelMarkers(),                   prevMarkers: this.previousBloodworkAnalysis?.getElectrolytePanelMarkers() ?? [] },
      { section: Section.LIVER_BILIARY,                 label: 'Liver',               markers: bloodworkAnalysis.getLiverPanelMarkers(),                         prevMarkers: this.previousBloodworkAnalysis?.getLiverPanelMarkers() ?? [] },
      { section: Section.LIPIDS,                        label: 'Lipids',              markers: bloodworkAnalysis.getLipidsPanelMarkers(),                        prevMarkers: this.previousBloodworkAnalysis?.getLipidsPanelMarkers() ?? [] },
      { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation',        markers: bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),   prevMarkers: this.previousBloodworkAnalysis?.getInflammationOxidativeStressPanelMarkers() ?? [] },
      { section: Section.THYROID,                       label: 'Thyroid',             markers: bloodworkAnalysis.getThyroidPanelMarkers(),                       prevMarkers: this.previousBloodworkAnalysis?.getThyroidPanelMarkers() ?? [] },
      { section: Section.SEX_HORMONE,                   label: 'Hormones',            markers: bloodworkAnalysis.getSexHormonePanelMarkers(),                    prevMarkers: this.previousBloodworkAnalysis?.getSexHormonePanelMarkers() ?? [] },
      { section: Section.PROSTATE,                      label: 'Prostate',            markers: bloodworkAnalysis.getProstatePanelMarkers(),                      prevMarkers: this.previousBloodworkAnalysis?.getProstatePanelMarkers() ?? [] },
    ];

    const colorLegendTooltip = (
      <Tooltip id="results-overview-tooltip">
        <div style={{ textAlign: 'left' }}>
          <strong>Button colors indicate the worst marker in that section:</strong>
          <br />
          <span style={{ color: '#78F880' }}>&#9632;</span> <strong>Green</strong> — all markers optimal
          <br />
          <span style={{ color: '#FEFA83' }}>&#9632;</span> <strong>Yellow</strong> — at least one marker outside optimal but within lab range
          <br />
          <span style={{ color: '#FFD37E' }}>&#9632;</span> <strong>Orange</strong> — at least one marker outside lab range but not in alarm range
          <br />
          <span style={{ color: '#FC6552' }}>&#9632;</span> <strong>Red</strong> — at least one marker in the alarm range
          <br /><br />
          Sections are ordered left to right — sections with alarm range markers on the left, sections with all optimal markers on the right.
          <br /><br />
          Click any button to jump to that section.
          <br /><br />
          The gray arrow button &#8679; in the bottom-right corner of the screen will bring you back here at any time.
        </div>
      </Tooltip>
    );

    return (
      <Card className="mb-3" id="results-overview">
        <Card.Header>
          Results Overview&nbsp;&nbsp;
          <OverlayTrigger overlay={colorLegendTooltip} placement="right">
            <FontAwesomeIcon icon="info-circle" style={{ color: '#fff', cursor: 'pointer' }} />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {(() => {
              const SEVERITY: Record<'red' | 'orange' | 'yellow' | 'green', number> = {
                red: 0, orange: 1, yellow: 2, green: 3,
              };
              const visibleSections = sections
                .filter(s => s.show !== false && s.markers.some(pm => pm.hasValue()))
                .map(s => ({
                  ...s,
                  status: this.getSectionStatus(s.markers),
                  prevStatus: s.prevMarkers.length > 0 ? this.getSectionStatus(s.prevMarkers) : null,
                }))
                .sort((a, b) => SEVERITY[a.status] - SEVERITY[b.status]);
              return visibleSections.map(({ section, label, status, prevStatus }, idx) => {
                const improved = prevStatus !== null && SEVERITY[status] > SEVERITY[prevStatus];
                const worsened = prevStatus !== null && SEVERITY[status] < SEVERITY[prevStatus];
                return (
                  <React.Fragment key={section}>
                    {idx > 0 && visibleSections[idx - 1].status !== status && (
                      <div style={{ width: '1px', backgroundColor: '#dee2e6', alignSelf: 'stretch', margin: '0 4px' }} />
                    )}
                    <Button
                      variant={statusToVariant(status)}
                      style={statusToStyle(status)}
                      size="sm"
                      onClick={() => {
                        if (this.state.collapsedSections.includes(section)) {
                          this.toggleSection(section);
                        }
                        document.getElementById(`section-${section}`)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      {label}
                      {improved && (
                        <FontAwesomeIcon
                          icon={faThumbsUp}
                          title="Improved"
                          style={{
                            marginLeft: 6,
                            color: status === 'green' ? '#fff' : '#1caa54',
                          }}
                        />
                      )}

                      {worsened && (
                        <FontAwesomeIcon
                          icon={faThumbsDown}
                          title="Worsened"
                          style={{
                            marginLeft: 6,
                            paddingTop: 1,
                            color: status === 'red' ? '#fff' : '#FC6552',
                          }}
                        />
                      )}
                    </Button>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </Card.Body>
      </Card>
    );
  }

  render() {
    console.log('BloodworkTestAnalysis.render......');
    const {
      bloodworkAnalysis,
      showUpgradeModal,
      hideTestResultsAnalysis,
      previousBloodwork,
      collapsedSections,
    } = this.state;
    const { furtherConsiderations, bloodworkEntryStore } = this.props;

    if (!bloodworkAnalysis) {
      return null;
    }

    const isCollapsed = (section: Section) => collapsedSections.includes(section);

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // Build Above/Below Optimal marker lists (same logic as the PDF view)
    const allMarkerSet = new Set<PanelMarker>([
      ...bloodworkAnalysis.getRBCPanelMarkers(),
      ...bloodworkAnalysis.getIronPanelMarkers(),
      ...bloodworkAnalysis.getWBCPanelMarkers(),
      ...bloodworkAnalysis.getPlateletPanelMarkers(),
      ...bloodworkAnalysis.getGlucosePanelMarkers(),
      ...bloodworkAnalysis.getKidneyPanelMarkers(),
      ...bloodworkAnalysis.getProteinsPanelMarkers(),
      ...bloodworkAnalysis.getMineralPanelMarkers(),
      ...bloodworkAnalysis.getElectrolytePanelMarkers(),
      ...bloodworkAnalysis.getLiverPanelMarkers(),
      ...bloodworkAnalysis.getLipidsPanelMarkers(),
      ...bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
      ...bloodworkAnalysis.getThyroidPanelMarkers(),
      ...bloodworkAnalysis.getSexHormonePanelMarkers(),
      ...bloodworkAnalysis.getProstatePanelMarkers(),
    ]);
    const allMarkers = Array.from(allMarkerSet);

    const isHigherIsBetter = (pm: PanelMarker) =>
      pm.isEGFR?.() || pm.field?.useEGFRChart;

    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;

    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    // Zone-penetration score: fraction of the relevant danger zone consumed.
    // WBC uses its own 9-zone percentFromMean which is already zone-normalized.
    // Falls back to |percentFromMean| when denominators collapse to zero.
    const zonePenetrationScore = (pm: PanelMarker, section: 'alarm' | 'lab' | 'optimal'): number => {
      const r = pm.range;
      if (!r) return Math.abs(pm.percentFromMean ?? 0);
      if (pm.field?.useWBCPercentFromMean) return Math.abs(pm.percentFromMean ?? 0);
      const v = pm.value;
      if (section === 'alarm') {
        const denom = r.stdDev && r.stdDev > 0 ? r.stdDev : 1;
        if (v > r.alarmMax) return (v - r.alarmMax) / denom;
        if (r.alarmMin > 0 && v < r.alarmMin) return (r.alarmMin - v) / denom;
        return 0;
      }
      if (section === 'lab') {
        if (v > r.labMax) {
          const denom = r.alarmMax - r.labMax;
          return denom > 0 ? (v - r.labMax) / denom : Math.abs(pm.percentFromMean ?? 0);
        }
        const denom = r.labMin - (r.alarmMin ?? 0);
        return denom > 0 ? (r.labMin - v) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      // section === 'optimal'
      if (v > (r.optimalMax ?? r.labMax)) {
        const denom = r.labMax - (r.optimalMax ?? r.labMax);
        return denom > 0 ? (v - r.optimalMax!) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      const optMin = r.optimalMin ?? r.labMin;
      const denomOpt = optMin - r.labMin;
      return denomOpt > 0 ? (optMin - v) / denomOpt : Math.abs(pm.percentFromMean ?? 0);
    };

    const alarmZoneDesc   = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'alarm')   - zonePenetrationScore(a, 'alarm');
    const labZoneDesc     = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'lab')     - zonePenetrationScore(a, 'lab');
    const optimalZoneDesc = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'optimal') - zonePenetrationScore(a, 'optimal');

    // "Alarm Range Markers" — outside alarm threshold in either direction, worst first
    const needsAttentionGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
          : (pm.isGreaterThanAlarm?.() || (!isRdwChart(pm) && pm.isLessThanAlarm?.()))
      ))
      .sort(alarmZoneDesc);

    // "Outside Lab Range Markers" — outside lab range but within alarm, worst first
    const worthDiscussingGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
          : (
              (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
              (!isRdwChart(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.())
            )
      ))
      .sort(labZoneDesc);

    // "Outside Optimal Range Markers" — outside optimal but within lab range, worst first
    const fineTuneGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
          : (
              (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
              (!isRdwChart(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.())
            )
      ))
      .sort(optimalZoneDesc);

    const systemGroupDefs = [
      { label: 'Blood Oxygen',       markers: bloodworkAnalysis.getRBCPanelMarkers?.() ?? [] },
      { label: 'Iron Status',        markers: bloodworkAnalysis.getIronPanelMarkers?.() ?? [] },
      { label: 'Immune',             markers: bloodworkAnalysis.getWBCPanelMarkers?.() ?? [] },
      { label: 'Blood Clotting',     markers: bloodworkAnalysis.getPlateletPanelMarkers?.() ?? [] },
      { label: 'Glucose',            markers: bloodworkAnalysis.getGlucosePanelMarkers?.() ?? [] },
      { label: 'Kidney',             markers: bloodworkAnalysis.getKidneyPanelMarkers?.() ?? [] },
      { label: 'Vitamins & Minerals', markers: bloodworkAnalysis.getMineralPanelMarkers?.() ?? [] },
      { label: 'Electrolytes',       markers: bloodworkAnalysis.getElectrolytePanelMarkers?.() ?? [] },
      { label: 'Proteins',           markers: bloodworkAnalysis.getProteinsPanelMarkers?.() ?? [] },
      { label: 'Liver',              markers: bloodworkAnalysis.getLiverPanelMarkers?.() ?? [] },
      { label: 'Lipids',             markers: bloodworkAnalysis.getLipidsPanelMarkers?.() ?? [] },
      { label: 'Inflammation',       markers: bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers?.() ?? [] },
      { label: 'Thyroid',            markers: bloodworkAnalysis.getThyroidPanelMarkers?.() ?? [] },
      { label: 'Hormones',           markers: bloodworkAnalysis.getSexHormonePanelMarkers?.() ?? [] },
      { label: 'Prostate',           markers: bloodworkAnalysis.getProstatePanelMarkers?.() ?? [] },
    ];
    const attrToSystem: Record<string, string> = {};
    systemGroupDefs.forEach(({ label, markers: sysMarkers }) => {
      sysMarkers.forEach(pm => { attrToSystem[pm.attribute] = label; });
    });
    const groupBySystem = (
      markers: PanelMarker[],
      scoreOf: (pm: PanelMarker) => number,
    ) => {
      const groupMap: Record<string, PanelMarker[]> = {};
      const groupBestScore: Record<string, number> = {};
      markers.forEach(pm => {
        const label = attrToSystem[pm.attribute] ?? 'Other';
        const score = scoreOf(pm);
        if (!groupMap[label]) { groupMap[label] = []; groupBestScore[label] = score; }
        else if (score > groupBestScore[label]) { groupBestScore[label] = score; }
        groupMap[label].push(pm);
      });
      return Object.keys(groupMap)
        .sort((a, b) => groupBestScore[b] - groupBestScore[a])
        .map(label => ({ systemLabel: label, markers: groupMap[label] }));
    };

    const collectionDate = bloodworkAnalysis.bloodwork.collectionDate;
    const client = bloodworkAnalysis.bloodwork.client;
    const age = bloodworkAnalysis.bloodwork.clientAgeAtCollectionDate();
    const panelTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about the items in this panel.
      </Tooltip>
    );

    const evalLarge = 3;
    const evalSmall = 12;

    const refLarge = 9;
    const refSmall = 12;

    const hideTestResultsAnalysisIconName = hideTestResultsAnalysis
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <>
        <Card id="test-results-analysis-panel">
          <Card.Header>
            <FontAwesomeIcon
              icon={hideTestResultsAnalysisIconName}
              onClick={() => this.toggleTestResultsAnalysis()}
            />
            &nbsp;&nbsp;Test Results Analysis&nbsp;
            <OverlayTrigger overlay={panelTooltip}>
              <FontAwesomeIcon
                icon="external-link-alt"
                onClick={this.openWindow}
              />
            </OverlayTrigger>
            <span className="float-right">
              <OverlayTrigger
                overlay={this.exportInfoTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon="info-circle"
                  onClick={this.openExportInfoWindow}
                />
              </OverlayTrigger>
              &nbsp;&nbsp;&nbsp;
              <OverlayTrigger overlay={this.exportTooltip()} placement="bottom">
                <FontAwesomeIcon
                  icon={faFileDownload}
                  onClick={() => this.export(false)}
                />
              </OverlayTrigger>
              &nbsp;&nbsp;&nbsp;
              <OverlayTrigger
                overlay={this.exportWithNotesTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon={faFileDownloadSolid}
                  onClick={() => this.export(true)}
                />
              </OverlayTrigger>
            </span>
          </Card.Header>
          {hideTestResultsAnalysis ? null : (
            <Card.Body>
              <Card>
                <Card.Header>Client Information</Card.Header>
                <Card.Body>
                  <div>
                    <Table striped bordered hover className="labsmartz-table">
                      <thead>
                        <tr>
                          <th>Last Name</th>
                          <th>First Name</th>
                          <th>Sex</th>
                          <th>Collection Date</th>
                          <th>Age at Collection</th>
                          <th>Date of Birth</th>
                          <th>Ethnicity</th>
                          {client.isFemale() && (
                            <th>Menopause/Pregnant</th>
                          )}
                          {client.isPregnant() && (
                            <th>Trimester</th>
                          )}
                          <th>Elevation</th>
                          <th>Inhaled Smoke</th>
                          <th>Alcohol Drinker</th>
                          {previousBloodwork && (
                            <th>Previous Collection Date</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="client-row">
                          <td>{client.lastName}</td>
                          <td>{client.firstName}</td>
                          <td>{client.displayGender}</td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {collectionDate}
                            </Moment>
                          </td>
                          <td>{age}</td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {client.dateOfBirth}
                            </Moment>
                          </td>
                          <td>{client.displayEthnicity}</td>
                          {client.isFemale() && (
                            <td>{client.displayMenopause}</td>
                          )}
                          {client.isPregnant() && (
                            <td>{client.displayTrimester}</td>
                          )}
                          <td>{client.displayElevation}</td>
                          <td>{client.displayInhaledSmoke}</td>
                          <td>{client.displayAlcoholDrinker}</td>
                          {previousBloodwork && (
                            <td>
                              <Moment format="MM/DD/YYYY">
                                {previousBloodwork.collectionDate}
                              </Moment>
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </Table>
                    {this.props.currentUserProfile.preparedBy && (
                      <div className="mt-2">
                        <strong>Prepared by:</strong>{' '}
                        {this.props.currentUserProfile.preparedBy}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
              {this.renderSectionSummary()}

              {process.env.REACT_APP_ENV !== 'prod' && (() => {
                const alarmN = needsAttentionGroup.length;
                const labN   = worthDiscussingGroup.length;
                const optN   = fineTuneGroup.length;
                // Stacking: only when all three have markers and one dominates both others combined
                const optDominant   = optN   > alarmN + labN && alarmN > 0 && labN > 0;
                const alarmDominant = alarmN >= labN + optN  && labN   > 0 && optN > 0;
                const GAP = '6px';

                const alarmCard = alarmN > 0 ? (
                  <Card className="summary-section-card" style={{ maxWidth: 600 }}>
                    <Card.Header onClick={() => this.toggleSection(Section.NEEDS_ATTENTION)} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={isCollapsed(Section.NEEDS_ATTENTION) ? 'chevron-down' : 'chevron-up'} />
                      &nbsp;&nbsp;Alarm Range Markers&nbsp;&nbsp;
                      <span onClick={e => e.stopPropagation()}>
                        <OverlayTrigger placement="right" overlay={
                          <Tooltip id="alarm-range-tooltip">
                            <div style={{ textAlign: 'left' }}>
                              These markers have values that have crossed into the alarm range.
                              <br /><br />
                              Sorted from most to least away from the alarm range limits.
                            </div>
                          </Tooltip>
                        }>
                          <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer' }} />
                        </OverlayTrigger>
                      </span>
                    </Card.Header>
                    {!isCollapsed(Section.NEEDS_ATTENTION) && (
                      <Card.Body>
                        <SummaryDashboardSection
                          title=""
                          section={Section.NEEDS_ATTENTION}
                          groups={groupBySystem(needsAttentionGroup, pm => zonePenetrationScore(pm, 'alarm'))}
                        />
                      </Card.Body>
                    )}
                  </Card>
                ) : null;

                const labCard = labN > 0 ? (
                  <Card className="summary-section-card" style={{ maxWidth: 600 }}>
                    <Card.Header onClick={() => this.toggleSection(Section.WORTH_DISCUSSING)} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={isCollapsed(Section.WORTH_DISCUSSING) ? 'chevron-down' : 'chevron-up'} />
                      &nbsp;&nbsp;Outside Lab Range Markers&nbsp;&nbsp;
                      <span onClick={e => e.stopPropagation()}>
                        <OverlayTrigger placement="right" overlay={
                          <Tooltip id="outside-lab-range-tooltip">
                            <div style={{ textAlign: 'left' }}>
                              These markers have values that fall outside the lab range but haven't crossed into the alarm range yet.
                              <br /><br />
                              Sorted from most to least away from the lab but below the alarm range limits.
                            </div>
                          </Tooltip>
                        }>
                          <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer' }} />
                        </OverlayTrigger>
                      </span>
                    </Card.Header>
                    {!isCollapsed(Section.WORTH_DISCUSSING) && (
                      <Card.Body>
                        <SummaryDashboardSection
                          title=""
                          section={Section.WORTH_DISCUSSING}
                          groups={groupBySystem(worthDiscussingGroup, pm => zonePenetrationScore(pm, 'lab'))}
                        />
                      </Card.Body>
                    )}
                  </Card>
                ) : null;

                const optCard = optN > 0 ? (
                  <Card className="summary-section-card" style={{ maxWidth: 600 }}>
                    <Card.Header onClick={() => this.toggleSection(Section.FINE_TUNE)} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={isCollapsed(Section.FINE_TUNE) ? 'chevron-down' : 'chevron-up'} />
                      &nbsp;&nbsp;Outside Optimal Range Markers&nbsp;&nbsp;
                      <span onClick={e => e.stopPropagation()}>
                        <OverlayTrigger placement="right" overlay={
                          <Tooltip id="outside-optimal-range-tooltip">
                            <div style={{ textAlign: 'left' }}>
                              These markers have values outside the optimal health range, but still within the lab range, so a standard lab report may not flag them as abnormal.
                              <br /><br />
                              Sorted from most to least away from the optimal but below the lab range limits.
                            </div>
                          </Tooltip>
                        }>
                          <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer' }} />
                        </OverlayTrigger>
                      </span>
                    </Card.Header>
                    {!isCollapsed(Section.FINE_TUNE) && (
                      <Card.Body>
                        <SummaryDashboardSection
                          title=""
                          section={Section.FINE_TUNE}
                          groups={groupBySystem(fineTuneGroup, pm => zonePenetrationScore(pm, 'optimal'))}
                        />
                      </Card.Body>
                    )}
                  </Card>
                ) : null;

                if (optDominant) {
                  return (
                    <div style={{ display: 'flex', gap: GAP, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: GAP }}>
                        {alarmCard}
                        {labCard}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>{optCard}</div>
                    </div>
                  );
                }
                if (alarmDominant) {
                  return (
                    <div style={{ display: 'flex', gap: GAP, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>{alarmCard}</div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: GAP }}>
                        {labCard}
                        {optCard}
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', gap: GAP, alignItems: 'flex-start' }}>
                    {alarmCard && <div style={{ flex: 1, minWidth: 0 }}>{alarmCard}</div>}
                    {labCard   && <div style={{ flex: 1, minWidth: 0 }}>{labCard}</div>}
                    {optCard   && <div style={{ flex: 1, minWidth: 0 }}>{optCard}</div>}
                  </div>
                );
              })()}

              <div id={`section-${Section.RBC}`}>
                <BloodworkTestAnalysisSection
                  section={Section.RBC}
                  panelTitle="Blood Oxygen & Red Blood Cell Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getRBCPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#rbc"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.RBC)}
                  onToggle={() => this.toggleSection(Section.RBC)}
                />
              </div>

              <div id={`section-${Section.IRON}`}>
                <BloodworkTestAnalysisSection
                  section={Section.IRON}
                  panelTitle="Iron Status & Utilization"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getIronPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#iron"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.IRON)}
                  onToggle={() => this.toggleSection(Section.IRON)}
                />
              </div>

              {hasWbcValue && (
                <div id={`section-${Section.WBC}`}>
                  <BloodworkTestAnalysisSection
                    section={Section.WBC}
                    panelTitle="Immune Activity"
                    bloodworkAnalysis={bloodworkAnalysis}
                    panelMarkers={bloodworkAnalysis.getWBCPanelMarkers()}
                    furtherConsiderations={furtherConsiderations}
                    evalSmall={evalSmall}
                    refSmall={refSmall}
                    evalLarge={evalLarge}
                    refLarge={refLarge}
                    infoLink="https://labsmarts.com/bibliography#wbc"
                    bloodworkEntryStore={bloodworkEntryStore}
                    enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                    collapsed={isCollapsed(Section.WBC)}
                    onToggle={() => this.toggleSection(Section.WBC)}
                  />
                </div>
              )}

              <div id={`section-${Section.PLATELETS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PLATELETS}
                  panelTitle="Blood Clotting Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getPlateletPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#platelets"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PLATELETS)}
                  onToggle={() => this.toggleSection(Section.PLATELETS)}
                />
              </div>

              <div id={`section-${Section.GLUCOSE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.GLUCOSE}
                  panelTitle="Glucose Regulation & Pancreatic Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getGlucosePanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#glucose"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.GLUCOSE)}
                  onToggle={() => this.toggleSection(Section.GLUCOSE)}
                />
              </div>

              <div id={`section-${Section.KIDNEY}`}>
                <BloodworkTestAnalysisSection
                  section={Section.KIDNEY}
                  panelTitle="Kidney Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getKidneyPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#kidney"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.KIDNEY)}
                  onToggle={() => this.toggleSection(Section.KIDNEY)}
                />
              </div>

              <div id={`section-${Section.PROTEINS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PROTEINS}
                  panelTitle="Protein Status"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getProteinsPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#proteins"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PROTEINS)}
                  onToggle={() => this.toggleSection(Section.PROTEINS)}
                />
              </div>

              <div id={`section-${Section.NUTRIENTS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.NUTRIENTS}
                  panelTitle="Vitamin & Mineral Status"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getMineralPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#nutrients"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.NUTRIENTS)}
                  onToggle={() => this.toggleSection(Section.NUTRIENTS)}
                />
              </div>

              <div id={`section-${Section.ELECTROLYTES}`}>
                <BloodworkTestAnalysisSection
                  section={Section.ELECTROLYTES}
                  panelTitle="Electrolyte Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getElectrolytePanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#electrolytes"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.ELECTROLYTES)}
                  onToggle={() => this.toggleSection(Section.ELECTROLYTES)}
                />
              </div>

              <div id={`section-${Section.LIVER_BILIARY}`}>
                <BloodworkTestAnalysisSection
                  section={Section.LIVER_BILIARY}
                  panelTitle="Liver Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getLiverPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#liver"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.LIVER_BILIARY)}
                  onToggle={() => this.toggleSection(Section.LIVER_BILIARY)}
                />
              </div>

              <div id={`section-${Section.LIPIDS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.LIPIDS}
                  panelTitle="Lipid Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getLipidsPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#lipids"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.LIPIDS)}
                  onToggle={() => this.toggleSection(Section.LIPIDS)}
                />
              </div>

              <div id={`section-${Section.INFLAMMATION_OXIDATIVE_STRESS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                  panelTitle="Inflammation & Oxidative Stress"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#inflammation"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  onToggle={() => this.toggleSection(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                />
              </div>

              <div id={`section-${Section.THYROID}`}>
                <BloodworkTestAnalysisSection
                  section={Section.THYROID}
                  panelTitle="Thyroid Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getThyroidPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#thyroid"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.THYROID)}
                  onToggle={() => this.toggleSection(Section.THYROID)}
                />
              </div>

              <div id={`section-${Section.SEX_HORMONE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.SEX_HORMONE}
                  panelTitle="Hormone Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getSexHormonePanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#hormones"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.SEX_HORMONE)}
                  onToggle={() => this.toggleSection(Section.SEX_HORMONE)}
                />
              </div>

              <div id={`section-${Section.PROSTATE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PROSTATE}
                  panelTitle="Prostate Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getProstatePanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#prostate"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PROSTATE)}
                  onToggle={() => this.toggleSection(Section.PROSTATE)}
                />
              </div>

              <Card>
                <Card.Header>Insights</Card.Header>
                <Card.Body>
                  <BloodworkNotes
                    section={Section.NOTES}
                    title="Add your own notes here. Click Save when finished."
                    bloodwork={bloodworkAnalysis.bloodwork}
                    bloodworkEntryStore={bloodworkEntryStore}
                  />
                </Card.Body>
              </Card>

              <DisclaimerSection furtherConsiderations={furtherConsiderations} />
            </Card.Body>
          )}
        </Card>

        <GenericModal
          show={showUpgradeModal}
          title="Export to PDF Feature Not Available"
          text="Export to PDF feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => {
            this.setState({ showUpgradeModal: false });
          }}
        />

        <button
          onClick={() => document.getElementById('results-overview')?.scrollIntoView({ behavior: 'smooth' })}
          title="Back to Results Overview"
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: '#766d66',
            //337ab7 - old blue color
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            lineHeight: '44px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            zIndex: 1050,
          }}
        >
          &#8679;
        </button>
      </>
    );
  }
}
